<script setup>
import { HUB } from '@/hub'
import { onMounted, ref, watch } from 'vue'
import { useStore } from "vuex";
import { Gems } from "@/gems";
import Menu from '../components/Menu'
import UsersInfoModal from '../components/UsersInfoModal.vue';


const users = ref([])
const selectedUser = ref([])
let showModal = ref(false)
const defaultImage = require('@/images/default.png')
const store = useStore()
let arrUsers
const userFilter = ref('all') // 'all', 'active', 'inactive', 'anonymized'
const searchValue = ref('')

async function GetUsers() {

  const response = await HUB.CallBackEnd({
    action: HUB.Action("get-users")
  });

  arrUsers = response.data
  ApplyFilters()
}

function normalizeString(str) {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function SearchUsers(value) {
  searchValue.value = value
  ApplyFilters()
}

function IsUserActive(user) {
  return Object.values(user.apps).reduce((prev, current) => prev || Number(current.active), 0) > 0
}

function GetUserOrderPriority(user) {
  if (IsUserActive(user)) return 1                 // Ativos
  if (Number(user.anonymized) === 1) return 3      // Anonimizados
  return 2                                         // Inativos
}

function ApplyFilters() {
  let filteredUsers = [...arrUsers]

  // Aplicar filtro por tipo
  switch (userFilter.value) {
    case 'active':
      filteredUsers = filteredUsers.filter(user => IsUserActive(user))
      break
    case 'inactive':
      filteredUsers = filteredUsers.filter(user => {
        const isActive = IsUserActive(user)
        const anonymized = user.anonymized !== undefined ? Number(user.anonymized) : 0
        return !isActive && anonymized === 0
      })
      break
    case 'anonymized':
      filteredUsers = filteredUsers.filter(user => {
        const anonymized = user.anonymized !== undefined ? Number(user.anonymized) : 0
        return !IsUserActive(user) && anonymized === 1
      })
      break
    case 'all':
    default:
      break
  }

  // Reordenar com base no estado atual (ativos > inativos > anonimizados) e depois por nome
  filteredUsers.sort((a, b) => {
    const priorityDiff = GetUserOrderPriority(a) - GetUserOrderPriority(b)
    if (priorityDiff !== 0) return priorityDiff
    return normalizeString(a.name).localeCompare(normalizeString(b.name))
  })

  // Aplicar filtro de pesquisa
  if (searchValue.value) {
    const normalizedValue = normalizeString(searchValue.value)
    filteredUsers = filteredUsers.filter((elem) => {
      return normalizeString(elem.internal).includes(normalizedValue)
        || normalizeString(elem.full_name).includes(normalizedValue)
        || normalizeString(elem.name).includes(normalizedValue)
    })
  }

  users.value = filteredUsers

  // Ajustar scrollbar
  const divUsersScrollbar = document.getElementById('div-users-list')
  if (divUsersScrollbar) {
    if (divUsersScrollbar.scrollHeight > divUsersScrollbar.clientHeight) {
      divUsersScrollbar.classList.add('div-without-scrollbar')
    } else {
      divUsersScrollbar.classList.remove('div-without-scrollbar')
    }
  }
}

async function ChangeUserStatus(user, type) {
  let status

  switch (type) {
    case 'set-active-status':
      user.active = !Number(user.active)
      status = user.active

      // Se o utilizador foi ativado, limpar o anonymized localmente para que apareça imediatamente na lista (sem necessidade de recarregar)
      if (status) {
        const topUser = arrUsers.find(u => u.id === user.user_id)
        if (topUser) topUser.anonymized = 0
      }

      if (user.user_id == store.getters.User.id)
        store.getters.User.apps[user.app].active = status
      break;

    case 'set-admin-status':
      user.admin = !Number(user.admin)
      status = user.admin

      if (user.user_id == store.getters.User.id)
        store.getters.User.apps[user.app].admin = status
      break;
  }

  const response = await HUB.CallBackEnd({
    action: HUB.Action(type),
    data: {
      app: user.app,
      status,
      user_id: user.user_id
    }
  })

  if (response.error) {
    GetUsers()
  } else if (type == 'set-active-status') {
    Gems.CallWS({ app: "kpi", action: "update-users-data" });
  }
}

function IsUserInactive(user) {
  return Object.values(user.apps).reduce((prev, current) => prev || Number(current.active), 0) == 0
}

function ControlUsersModal(user = null, modalInfo = showModal.value) {
  const modalValue = modalInfo;

  if (!user) {
    selectedUser.value = {};
  } else {
    selectedUser.value = user;
  }

  if (modalValue) {
    showModal.value = false
  } else {
    showModal.value = true
  }
}

function UpdateUserRow() {
  GetUsers()
}

// Watcher para detectar mudanças no filtro
watch(userFilter, () => {
  ApplyFilters()
})

onMounted(() => {
  GetUsers()
  Gems.Tooltip(document.getElementById('icon-add-user'), "Novo utilizador", "tooltip");
})
</script>


<template>
  <div>
    <div id="div-container" class="main-container-background">

      <Menu></Menu>

      <div class="page-container">

        <div class="div-search-users">
          <img class="img-inpeople-default" src="../images/inpeople.png">
          <div class="div-filters-container">
            <div class="div-filter-select">
              <label for="user-filter">Filtrar por</label>
              <select id="user-filter" v-model="userFilter">
                <option value="all">--</option>
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
                <option value="anonymized">Anonimizados</option>
              </select>
            </div>
            <div class="div-search-input">
              <label for="search-users">Pesquisar</label>
              <input type="text" id="search-users" @keyup="SearchUsers($event.target.value)">
            </div>
            <div>
              <i id="icon-add-user" class="fa-solid fa-plus" @click="ControlUsersModal()"></i>
            </div>
          </div>
        </div>

        <div id="div-user-data" class="div-users-list-container">

          <div id="div-user-header">
            <div></div>
            <div class="div-user-internal">N interno</div>
            <div>Nome completo</div>
            <div>Nome profissional</div>

            <div v-for="(app, i) in store.getters.Apps" :key="i">
              <div>{{ app }}</div>

              <div class="div-toggle-header">
                <span>(ativo)</span>
                <span>(admin)</span>
              </div>
            </div>
          </div>

          <div class="div-users-list scrollbar" id="div-users-list">
            <div class="div-user-container" :class="{ 'user-alt': ind % 2 == 0 }" v-for="(user, ind) in users"
              :key="user.id">

              <div class="div-left-user-data" :class="{ 'inactive-user': IsUserInactive(user) }"
                @click="ControlUsersModal(user)">
                <img id="div-user-photo" class="div-user-photo" :src="user.src ?? defaultImage"
                  @error="$event.target.src = defaultImage">

                <div class="div-user-internal">
                  {{ user.internal }}
                </div>
                <div class="div-user-fullname">
                  {{ user.full_name }}
                </div>
                <div class="div-user-professional-name">
                  {{ user.name }}
                </div>
              </div>

              <div class="div-apps-container" v-for="(appName, i) in store.getters.Apps" :key="i">
                <div>
                  <i @click="ChangeUserStatus(user.apps[appName], 'set-active-status')"
                    class="fa clickable fa-toggle-off active" :class="{
                      'inactive': user.apps[appName].active == 0,
                      'disabled': !store.getters.User.apps[appName] || store.getters.User.apps[appName].active == 0 || store.getters.User.apps[appName].admin == 0
                    }"></i>

                  <i @click="ChangeUserStatus(user.apps[appName], 'set-admin-status')"
                    class="fa clickable fa-toggle-off admin" :class="{
                      'inactive': user.apps[appName].admin == 0,
                      'disabled': !store.getters.User.apps[appName] || store.getters.User.apps[appName].active == 0 || store.getters.User.apps[appName].admin == 0
                    }"></i>
                </div>
              </div>

            </div>
          </div>


        </div>
      </div>
    </div>
    <UsersInfoModal v-if="showModal" :userId="selectedUser?.id" @control-user-modal="ControlUsersModal()"
      @update-user-data="UpdateUserRow">
    </UsersInfoModal>
  </div>

</template>

<style scoped>
#div-container {
  display: flex;
  min-height: 100vh;
}

@media only screen and (max-width: 768px) {
  #div-container {
    overflow: auto;
  }

  .page-container {
    width: initial !important;
  }
}

.fa-plus {
  transform: scale(2);
  margin-right: 10px;
  background: linear-gradient(310deg, rgb(205 176 255) 38%, rgb(135 255 165) 51%, rgb(24 232 255) 45%, rgb(69 77 255) 69%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  cursor: pointer;
}

.fa-plus:hover {
  background: var(--default-hover-color);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.div-filters-container {
  display: flex;
  justify-content: flex-end;
  gap: 20px;
  width: 40%;
}

.div-filter-select {
  display: flex;
  gap: 5px;
  height: 30%;
  align-items: center;
}

.div-filter-select label {
  white-space: nowrap;
}

.div-filter-select select {
  border-radius: 5px;
  padding: 4px 8px;
  margin-left: 2%;
  background-color: #1a1a1a;
  color: #ffffff;
  border: 1px solid #3a3a3a;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  min-width: 140px;
}

.div-filter-select select:hover {
  border-color: rgb(135 255 165);
}

.div-filter-select select:focus {
  outline: none;
  border-color: rgb(135 255 165);
  box-shadow: 0 0 5px rgba(135, 255, 165, 0.3);
}

.div-filter-select option {
  background-color: #1a1a1a;
  color: #ffffff;
  padding: 5px;
}

.div-without-scrollbar {
  padding-right: 5px;
}

.div-search-users {
  display: flex;
  justify-content: space-between;
}

.div-search-input {
  display: flex;
  gap: 5px;
  height: 30%;
  min-width: 300px;
  align-items: center;
}

.div-search-input input {
  border-radius: 5px;
  padding: 7px 8px;
  width: 100%;
  margin-left: 2%;
  font-size: 14px;
}

.page-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 25px 35px 25px 14px;
}

.logo-inpeople {
  width: 10%;
  margin-bottom: calc(.8% + 28px);
  opacity: .3;
}

.div-users-list-container {
  width: 100%;
  margin: auto;
  overflow-y: hidden;
}

.div-users-list {
  overflow-y: auto;
  height: 75vh;
  border-radius: 7px;
  font-weight: 300;
}

.div-user-container {
  display: grid;
  grid-template-columns: 71% 29%;
  align-items: center;
  border-radius: 5px;
}

.div-user-fullname {
  align-content: space-evenly;
}

.inactive-user {
  opacity: 0.3;
}

.inactive {
  color: #808090 !important;
}

.user-alt {
  background-color: #231f20;
}

#div-user-header {
  font-family: "Space Grotesk", sans-serif;
  text-transform: uppercase;
  font-weight: bolder;
  letter-spacing: 0.1em;
  background: linear-gradient(45deg, rgba(135, 255, 165, 1), rgba(205, 176, 255, 1) 55%, rgb(188 118 201) 75%);
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.div-user-container,
#div-user-header {
  display: grid;
  grid-template-columns: 30px .7fr 1.6fr 1.3fr repeat(5, .2fr);
  gap: .2%;
  justify-content: center;
  padding: 5px 10px;
}

.div-left-user-data {
  display: grid;
  grid-template-columns: 38px .7fr 1.6fr 1.3fr;
  align-items: center;
  cursor: pointer;
}

.div-user-container {
  grid-template-columns: 3.8fr repeat(5, .2fr)
}

.div-apps-container div {
  display: flex;
}

.div-apps-container i {
  font-size: 1.5em;
  width: 50px;
  text-align: center;
}

.div-user-photo {
  height: 38px;
  width: 38px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  box-shadow: 0 3px 4px rgb(0 0 0 / 10%), 0 4px 10px rgb(0 0 0 / 20%);
  vertical-align: bottom;
  object-fit: cover;
}

.div-toggle-header {
  font-size: 0.6rem;
  text-transform: lowercase;
  display: flex;
  -webkit-text-fill-color: lightgray;
}

.div-toggle-header span {
  width: 50px;
}

.div-user-internal {
  text-align: center;
}

.active,
.admin {
  rotate: 180deg;
}

.inactive,
.not-admin {
  rotate: 0deg;
}

.active {
  color: rgb(135 255 165);
}

.not-admin {
  color: rgb(205 176 255) !important;
}

@media screen and (min-width: 2160px) {
  .div-left-user-data {
    grid-template-columns: 38px .7fr 1.6fr 1.29fr;
  }

  .div-user-container {
    grid-template-columns: 3.68fr repeat(5, .2fr);
  }
}
</style>
